import { useEffect, useState, useCallback, useMemo } from "react";
import { getUsageSummary, getUsageHeatmap, getUsageTeams, refreshUsageDashboard } from "../api";

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmtUsd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${Number(n || 0)}%`;

// ── Icons ──────────────────────────────────────────────────────────────────────
const GitHubIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
  </svg>
);
const JiraIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84a.84.84 0 0 0-.84-.84zM6.77 6.8a4.36 4.36 0 0 0 4.36 4.34h1.78v1.72a4.36 4.36 0 0 0 4.34 4.34V7.63a.84.84 0 0 0-.83-.83zM2 11.6c0 2.4 1.95 4.34 4.35 4.34h1.78v1.72c.01 2.4 1.95 4.34 4.35 4.34v-9.57a.84.84 0 0 0-.84-.83z"/>
  </svg>
);

// ── Heatmap color ramps — deliberately different hues for the two views ───────
const CLAUDE_RAMP  = ["#e6ebf5", "#b7c6ec", "#82a0e0", "#4a72d1", "#2148ad"]; // blue  (matches --primary)
const SHIPPED_RAMP = ["#e3f5ec", "#a9e3c4", "#63c895", "#2ea36c", "#166b45"]; // emerald

function levelFor(count, max) {
  if (!count) return 0;
  if (!max) return 0;
  const ratio = count / max;
  if (ratio > 0.8) return 4;
  if (ratio > 0.55) return 3;
  if (ratio > 0.3) return 2;
  return 1;
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""];

function Heatmap({ byDate, max, ramp }) {
  const { weeks, monthMarks } = useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 370);
    const dow = start.getDay();
    start.setDate(start.getDate() - (dow === 0 ? 6 : dow - 1)); // snap to Monday

    const weeks = [];
    const monthMarks = [];
    let lastMonth = -1;
    let cursor = new Date(start);
    let weekIdx = 0;

    while (cursor <= today) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const iso = cursor.toISOString().slice(0, 10);
        week.push({ iso, count: byDate[iso] || 0, isWeekend: d === 5 || d === 6 });
        if (d === 0 && cursor.getMonth() !== lastMonth) {
          lastMonth = cursor.getMonth();
          monthMarks.push({ week: weekIdx, label: MONTHS[lastMonth] });
        }
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(week);
      weekIdx++;
    }
    return { weeks, monthMarks };
  }, [byDate]);

  const cell = 11, gap = 2, step = cell + gap, left = 30, top = 16;
  const width = left + weeks.length * step;
  const height = top + 5 * step; // Mon..Fri only, matches the reference mock

  return (
    <div style={{ overflowX: "auto", paddingBottom: 4 }}>
      <svg width={width} height={height} style={{ display: "block", minWidth: width }}>
        {monthMarks.map((m, i) => (
          <text key={i} x={left + m.week * step} y={10} fontSize="10" fill="var(--muted)">{m.label}</text>
        ))}
        {DAY_LABELS.slice(0, 5).map((lbl, i) => lbl ? (
          <text key={i} x={0} y={top + i * step + cell * 0.8} fontSize="10" fill="var(--muted)">{lbl}</text>
        ) : null)}
        {weeks.map((week, w) => week.slice(0, 5).map((day, d) => (
          <rect
            key={`${w}-${d}`}
            x={left + w * step} y={top + d * step}
            width={cell} height={cell} rx={2}
            fill={ramp[levelFor(day.count, max)]}
          >
            <title>{`${day.iso}: ${day.count}`}</title>
          </rect>
        )))}
      </svg>
    </div>
  );
}

const SOURCES = [
  { id: "all",    label: "All sources", icon: null },
  { id: "github", label: "GitHub",      icon: GitHubIcon },
  { id: "jira",   label: "Jira",        icon: JiraIcon },
];

function SourceTabs({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
      {SOURCES.map((s) => {
        const active = value === s.id;
        return (
          <button
            key={s.id}
            onClick={() => onChange(s.id)}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              fontFamily: "inherit", fontSize: 12.5, fontWeight: 500,
              padding: "4px 11px", borderRadius: 20, cursor: "pointer",
              border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
              background: active ? "var(--primary-light)" : "transparent",
              color: active ? "var(--primary)" : "var(--muted)",
            }}
          >
            {s.icon && <s.icon />}
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Insights: Claude use vs. Shipped work, org-wide + team-level ───────────────
// Computed straight from data already on screen (the two heatmap series + the
// teams table) — no extra API calls, no invented numbers.
function last30Dates() {
  const dates = [];
  const d = new Date();
  for (let i = 0; i < 30; i++) {
    dates.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() - 1);
  }
  return dates;
}

function Pill({ label, color, bg }) {
  return (
    <span style={{
      fontSize: 11.5, fontWeight: 600, color, background: bg,
      borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function InsightsBox({ claudeByDate, shippedByDate, sourceLabel, teams }) {
  const orgInsight = useMemo(() => {
    const days = last30Dates();
    let usedDays = 0, shippedDays = 0, bothDays = 0;
    for (const d of days) {
      const used = (claudeByDate[d] || 0) > 0;
      const shipped = (shippedByDate[d] || 0) > 0;
      if (used) usedDays++;
      if (shipped) shippedDays++;
      if (used && shipped) bothDays++;
    }
    const conversionPct = usedDays ? Math.round((bothDays / usedDays) * 100) : 0;

    let verdict;
    if (usedDays === 0) {
      verdict = "No Claude usage recorded in the last 30 days yet.";
    } else if (conversionPct >= 70) {
      verdict = "Usage is consistently converting into shipped work.";
    } else if (conversionPct >= 40) {
      verdict = "About half of active days ship something — there's room to close the gap.";
    } else {
      verdict = "Most active days aren't yet producing shipped work — worth digging into blockers.";
    }

    return { usedDays, shippedDays, bothDays, conversionPct, verdict };
  }, [claudeByDate, shippedByDate]);

  // Team-level callouts: who's converting usage into shipped work best/worst, and
  // who's over budget. Only computed from teams with any recorded activity, so an
  // idle team doesn't get flagged as "needs attention" for having 0% of nothing.
  const teamInsight = useMemo(() => {
    const activeTeams = (teams || []).filter((t) => t.active > 0);
    if (activeTeams.length === 0) return null;

    const best = [...activeTeams].sort((a, b) => b.shipped - a.shipped)[0];
    const worst = [...activeTeams].sort((a, b) => a.shipped - b.shipped)[0];
    const overBudget = (teams || []).filter((t) => t.budget > 100);
    const showGap = best && worst && best.name !== worst.name;

    return { best, worst, overBudget, showGap };
  }, [teams]);

  return (
    <div style={{
      padding: "12px 16px", borderRadius: 10, marginBottom: 16,
      background: "linear-gradient(90deg, var(--primary-light) 0%, var(--success-light) 100%)",
      border: "1px solid var(--border)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 18, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: "#2148ad", textTransform: "uppercase", letterSpacing: 0.3 }}>Claude use</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#2148ad" }}>{orgInsight.usedDays}<span style={{ fontSize: 11, fontWeight: 500, color: "var(--muted)" }}> / 30 days</span></div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: "#166b45", textTransform: "uppercase", letterSpacing: 0.3 }}>Shipped work</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#166b45" }}>{orgInsight.shippedDays}<span style={{ fontSize: 11, fontWeight: 500, color: "var(--muted)" }}> / 30 days</span></div>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text)", textTransform: "uppercase", letterSpacing: 0.3 }}>Conversion</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>{orgInsight.conversionPct}%</div>
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text)", lineHeight: 1.4, flex: 1, minWidth: 200 }}>
          {orgInsight.verdict}
          {sourceLabel !== "All sources" && (
            <span style={{ color: "var(--muted)" }}> ({sourceLabel} only.)</span>
          )}
        </div>
      </div>

      {teamInsight && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)",
        }}>
          <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3 }}>
            By team
          </span>
          <Pill
            label={`${teamInsight.best.name} ships the most (${teamInsight.best.shipped}%)`}
            color="#166b45" bg="var(--success-light)"
          />
          {teamInsight.showGap && teamInsight.worst.shipped < 50 && (
            <Pill
              label={`${teamInsight.worst.name} needs attention (${teamInsight.worst.shipped}% shipped, ${teamInsight.worst.active}% active)`}
              color="var(--danger)" bg="var(--danger-light)"
            />
          )}
          {teamInsight.overBudget.length > 0 && (
            <Pill
              label={
                teamInsight.overBudget.length === 1
                  ? `${teamInsight.overBudget[0].name} is over budget (${teamInsight.overBudget[0].budget}%)`
                  : `${teamInsight.overBudget.length} teams are over budget`
              }
              color="var(--warning)" bg="var(--warning-light)"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function UsageDashboard({ onNavigate }) {
  const [summary, setSummary]   = useState(null);
  const [teams, setTeams]       = useState([]);
  const [heatmapClaude, setHeatmapClaude]   = useState({ by_date: {}, max: 0, caption: "", connected: true });
  const [heatmapShipped, setHeatmapShipped] = useState({ by_date: {}, max: 0, caption: "", connected: true });
  const [view, setView]         = useState("claude");     // claude | shipped — controls which series is drawn
  const [heatSource, setHeatSource] = useState("all");
  const [teamSource, setTeamSource] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const loadSummary = useCallback(() => { getUsageSummary().then(setSummary).catch(() => {}); }, []);
  const loadTeams   = useCallback((src) => { getUsageTeams({ source: src }).then(setTeams).catch(() => {}); }, []);
  const loadHeatmaps = useCallback((src) => {
    getUsageHeatmap({ view: "claude", source: src }).then(setHeatmapClaude).catch(() => {});
    getUsageHeatmap({ view: "shipped", source: src }).then(setHeatmapShipped).catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadHeatmaps(heatSource); }, [heatSource, loadHeatmaps]);
  useEffect(() => { loadTeams(teamSource); }, [teamSource, loadTeams]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshUsageDashboard();
      loadSummary();
      loadHeatmaps(heatSource);
      loadTeams(teamSource);
    } catch { /* silent — nightly job will catch up anyway */ }
    setRefreshing(false);
  };

  const active = view === "claude" ? heatmapClaude : heatmapShipped;
  const ramp = view === "claude" ? CLAUDE_RAMP : SHIPPED_RAMP;
  const sourceLabel = SOURCES.find((s) => s.id === heatSource)?.label || "All sources";

  return (
    <>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1>Track usage and results</h1>
          <p>
            {summary?.integrations_connected != null
              ? `${summary.integrations_connected} of ${summary.integrations_total} integrations connected. `
              : ""}
            Refreshes automatically every night — or refresh now.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            fontFamily: "inherit", fontSize: 13, fontWeight: 600,
            padding: "8px 16px", borderRadius: 8, cursor: refreshing ? "default" : "pointer",
            border: "1px solid var(--border)", background: "var(--surface)",
            color: "var(--text)", opacity: refreshing ? 0.6 : 1,
          }}
        >
          {refreshing ? "Refreshing…" : "Refresh now"}
        </button>
      </div>

      <div className="page-body">
        {/* ── KPI tiles ──────────────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
          <div className="card" style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>Spend this month</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{fmtUsd(summary?.spend_this_month_usd)}</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>
              {fmtPct(summary?.spend_pct)} of {fmtUsd(summary?.monthly_budget_usd)}
              {summary?.spend_source === "internal" && (
                <span title="Add ANTHROPIC_ANALYTICS_KEY for authoritative Anthropic-reported spend"> · estimated</span>
              )}
            </div>
          </div>

          <div className="card" style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>People using Claude</div>
            <div style={{ fontSize: 26, fontWeight: 700, display: "flex", alignItems: "baseline", gap: 8 }}>
              {summary?.people_active ?? "—"}
              <span style={{ fontSize: 11, fontWeight: 600, background: "var(--primary-light)", color: "var(--primary)", borderRadius: 6, padding: "2px 7px" }}>
                {summary?.integrations_connected ?? 0} · Track
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>of {summary?.seats_total ?? "—"} seats</div>
          </div>

          <div className="card" style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>Usage that shipped work</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "var(--success)" }}>{fmtPct(summary?.shipped_pct)}</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>of active days</div>
          </div>

          <div className="card" style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>Needs a decision</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "var(--danger)" }}>{summary?.needs_decision ?? "—"}</div>
            {/* Scoped to Credit Requests only (pending + manager_approved) — Claude/AI
                use-case requests have their own queue on the All Requests page and are
                not counted here. Deep-links straight to the "Pending" tab per request;
                note this tab alone won't show "manager_approved" (At Admin) items also
                included in this count — those are one tab over. */}
            {summary?.needs_decision ? (
              <button
                onClick={() => onNavigate && onNavigate("creditrequests", { status: "pending" })}
                style={{
                  fontFamily: "inherit", fontSize: 12, color: "var(--muted)", marginTop: 6,
                  textDecoration: "underline", cursor: "pointer", background: "none", border: "none", padding: 0,
                }}
              >
                pending credit request{summary.needs_decision === 1 ? "" : "s"}
              </button>
            ) : (
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>No pending requests</div>
            )}
          </div>
        </div>

        {/* ── Heatmap ────────────────────────────────────────────────────── */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Every day, last 12 months</span>
            <div style={{ display: "flex", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 2, gap: 2 }}>
              {["claude", "shipped"].map((v) => (
                <button key={v} onClick={() => setView(v)} style={{
                  fontFamily: "inherit", fontSize: 12.5, fontWeight: 500, padding: "5px 12px",
                  border: "none", borderRadius: 6, cursor: "pointer",
                  background: view === v ? "var(--surface)" : "transparent",
                  color: view === v ? "var(--text)" : "var(--muted)",
                  boxShadow: view === v ? "0 1px 3px rgba(0,0,0,.08)" : "none",
                }}>
                  {v === "claude" ? "Claude use" : "Shipped work"}
                </button>
              ))}
            </div>
          </div>

          <SourceTabs value={heatSource} onChange={setHeatSource} />

          <InsightsBox
            claudeByDate={heatmapClaude.by_date}
            shippedByDate={heatmapShipped.by_date}
            sourceLabel={sourceLabel}
            teams={teams}
          />

          <Heatmap byDate={active.by_date} max={active.max} ramp={ramp} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <span style={{ fontSize: 11.5, color: "var(--muted)" }}>{active.caption}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11.5, color: "var(--muted)" }}>
              <span>Less</span>
              {ramp.map((c, i) => <div key={i} style={{ width: 10, height: 10, borderRadius: 2, background: c }} />)}
              <span>More</span>
            </div>
          </div>
        </div>

        {/* ── Teams ──────────────────────────────────────────────────────── */}
        <div className="card" style={{ padding: 20, marginTop: 16 }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Teams</div>
            <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>
              Active: share of the team's people who've submitted a request. Moving/Shipped: GitHub merged PRs + Jira resolved
              tickets for this team, relative to the busiest team (source-filterable above). Budget used: against an even split of the monthly budget.
            </div>
          </div>

          <SourceTabs value={teamSource} onChange={setTeamSource} />

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", fontSize: 11.5, fontWeight: 500, color: "var(--muted)", paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>Team</th>
                  {["Active", "Moving", "Shipped", "Budget used"].map((h) => (
                    <th key={h} style={{ textAlign: "right", fontSize: 11.5, fontWeight: 500, color: "var(--muted)", paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.name}>
                    <td style={{ padding: "11px 0", borderBottom: "1px solid var(--border)", fontWeight: 600, fontSize: 13.5 }}>{t.name}</td>
                    {["active", "moving", "shipped", "budget"].map((key) => {
                      const val = t[key];
                      const isBudget = key === "budget";
                      const color = isBudget
                        ? (val > 100 ? "var(--danger)" : val > 82 ? "var(--warning)" : "var(--text)")
                        : (val >= 80 ? "var(--text)" : val >= 50 ? "var(--warning)" : "var(--danger)");
                      const barColor = isBudget
                        ? (val > 100 ? "var(--danger)" : val > 82 ? "var(--warning)" : "var(--success)")
                        : (val >= 80 ? "var(--success)" : val >= 50 ? "var(--warning)" : "var(--danger)");
                      return (
                        <td key={key} style={{ padding: "11px 0", borderBottom: "1px solid var(--border)", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 500, color, fontVariantNumeric: "tabular-nums" }}>{val}%</span>
                            <div style={{ height: 4, width: 72, background: "var(--border)", borderRadius: 2 }}>
                              <div style={{ height: "100%", width: `${Math.min(val, 100)}%`, background: barColor, borderRadius: 2 }} />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {teams.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: "20px 0", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>No teams yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
